<script setup>
import { ref, onMounted } from 'vue';

const open = ref(false);
const webinars = ref([]);
const loading = ref(true);

const checkAuth = () => {
    try {
        axios.get(route('webinars.index')).then((response) => {
            webinars.value = response.data.data;
            loading.value = false;
            open.value = !!response.data.shouldOpen;
        });
    } catch (e) {
        console.log(e);
    }
};

const timeUntilJoinable = (startTime) => {
    const start = new Date(startTime);
    const joinTime = new Date(start.getTime());
    const now = new Date();

    if (now >= joinTime) {
        return 'now';
    }

    const diff = joinTime.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
        return `in ${hours}h ${minutes}m`;
    } else {
        return `in ${minutes}m`;
    }
};

const joinWebinar = (url) => {
    window.open(url, '_blank');
};

const register = (webinarId) => {
    axios
        .post(route('webinars.register'), {
            webinar_id: webinarId,
        })
        .then((response) => {
            webinars.value.find(
                (webinar) => webinar.id === webinarId
            ).registered = true;
        })
        .catch((error) => {
            console.error(error);
        });
};

const viewDetails = (url) => {
    window.open(url, '_blank');
};

const onClose = () => {
    open.value = false;
};

onMounted(() => {
    checkAuth();
});
</script>
<template>
    <div>
        <button-base
            @click="open = true"
            :badge="webinars.filter((webinar) => !webinar.registered).length"
            :badgeColor="'bg-red-600'"
            >Next Up Live</button-base
        >

        <slideout-with-slots
            v-if="open"
            title="Next Up Live"
            @close="onClose"
            max-width-class="max-w-4xl"
        >
            <template #body>
                <div
                    class="text-sm italic text-center text-gray-800 bg-gray-100 -mx-4 md:-mx-6 py-4 -mt-6 border-b"
                >
                    <p class="mb-0">
                        Live events are free to attend for you as a myflow user.
                    </p>
                </div>

                <div v-if="loading" class="py-8 text-center">
                    <i
                        class="block mb-3 text-3xl fa-regular fa-calendar text-gray-300"
                    ></i>
                    <p class="text-gray-600">Loading upcoming events...</p>
                </div>
                <div
                    v-if="!loading && webinars.length === 0"
                    class="py-8 text-center"
                >
                    <i
                        class="block mb-3 text-3xl fa-regular fa-calendar text-gray-300"
                    ></i>
                    <p class="text-gray-600">
                        There are no upcoming events to show here...
                    </p>
                </div>

                <list-basic v-else list-class="-mx-4 md:-mx-6">
                    <list-basic-item
                        v-for="webinar in webinars"
                        :key="webinar.id"
                    >
                        <div class="flex justify-between space-y-1">
                            <div>
                                <div class="flex flex-col space-y-1">
                                    <div
                                        class="w-full flex flex-col md:flex-row md:space-x-2 mb-1 md:mb-0"
                                    >
                                        <div class="w-full">
                                            <h3
                                                class="text-lg font-semibold text-gray-900"
                                            >
                                                {{ webinar.title }}
                                            </h3>
                                            <span
                                                v-if="webinar.registered"
                                                class="pb-1 text-xs text-green-800"
                                            >
                                                <i
                                                    class="fa-regular fa-user-check"
                                                ></i>
                                                Registered
                                            </span>
                                            <span
                                                v-if="!webinar.registered"
                                                class="pb-1 text-xs text-red-500"
                                            >
                                                Not registered yet
                                            </span>
                                        </div>
                                        <div
                                            class="flex justify-end space-x-2 md:space-x-0 md:space-y-2 shrink-0 md:flex md:flex-col mt-2 md:mt-0"
                                        >
                                            <button-success
                                                v-if="webinar.webinar_url"
                                                size="sm"
                                                @click="
                                                    joinWebinar(
                                                        webinar.webinar_url
                                                    )
                                                "
                                            >
                                                <i
                                                    class="mr-1 fa-regular fa-video"
                                                ></i>
                                                Join events
                                            </button-success>

                                            <button-secondary
                                                v-if="webinar.page_url"
                                                size="sm"
                                                @click="
                                                    viewDetails(
                                                        webinar.page_url
                                                    )
                                                "
                                            >
                                                <i
                                                    class="mr-1 fa-regular fa-eye"
                                                ></i>
                                                Read more
                                            </button-secondary>

                                            <button-secondary
                                                v-if="!webinar.registered"
                                                size="sm"
                                                @click="register(webinar.id)"
                                            >
                                                <i
                                                    class="mr-1 fa-regular fa-user-plus"
                                                ></i>
                                                Register
                                            </button-secondary>

                                            <div
                                                class="flex items-center text-xs text-gray-700"
                                                v-if="
                                                    !webinar.webinar_url &&
                                                    webinar.registered
                                                "
                                            >
                                                <i
                                                    class="mr-1 fa-regular fa-hourglass-half"
                                                ></i>
                                                <span
                                                    >Join in
                                                    {{
                                                        timeUntilJoinable(
                                                            webinar.utc_start_time
                                                        )
                                                    }}</span
                                                >
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        class="grid grid-cols-3 gap-2 py-2 lg:py-0 lg:flex flex-col md:flex-row lg:items-center lg:space-x-6 text-xs text-gray-600 bg-gray-50 border-y fill-sidebar-padding md:border-y-0 md:bg-transparent"
                                    >
                                        <div
                                            class="flex items-center space-x-1"
                                        >
                                            <i
                                                class="fa-regular fa-calendar text-myflowPurple-200"
                                            ></i>
                                            <span>{{
                                                webinar.user_start_date
                                            }}</span>
                                        </div>
                                        <div
                                            class="flex items-center space-x-1"
                                        >
                                            <i
                                                class="fa-regular fa-clock text-myflowPurple-200"
                                            ></i>
                                            <span>{{
                                                webinar.user_start_time
                                            }}</span>
                                        </div>
                                        <div
                                            class="flex items-center space-x-1"
                                        >
                                            <i
                                                class="fa-regular fa-timer text-myflowPurple-200"
                                            ></i>
                                            <span>{{ webinar.duration }}</span>
                                        </div>
                                        <div
                                            class="flex items-center space-x-1"
                                            v-if="webinar.language"
                                        >
                                            <i
                                                class="fa-regular fa-globe text-myflowPurple-200"
                                            ></i>
                                            <span>{{
                                                webinar.language.toUpperCase()
                                            }}</span>
                                        </div>
                                        <div
                                            class="col-span-2 flex items-center space-x-1"
                                            v-if="webinar.language"
                                        >
                                            <i
                                                class="fa-regular fa-video text-myflowPurple-200"
                                            ></i>
                                            <span>Zoom</span>
                                        </div>
                                    </div>
                                </div>

                                <div class="mt-2">
                                    <p
                                        class="text-sm text-gray-700"
                                        v-if="webinar.description"
                                        v-html="
                                            webinar.description.replace(
                                                /\n/g,
                                                '<br>'
                                            )
                                        "
                                    ></p>
                                </div>
                            </div>
                        </div>
                    </list-basic-item>
                </list-basic>
            </template>
        </slideout-with-slots>
    </div>
</template>
